import React, {
  ComponentType,
  createContext,
  Fragment,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Button,
  DescriptionList,
  Dialog,
  Flex,
  GridAlpha,
  Heading,
  Link,
  Tag,
  Text,
  Tooltip,
  useHasElementScrolledOutOfView,
  Markdown,
} from '@applyboard/crystal-ui';
import {
  ArrowRightOutlineIcon,
  InfoOutlineIcon,
  SchoolHifiIcon,
  WarningOutlineIcon,
  PersonTieRelationshipOutlineIcon,
  PersonOutlineIcon,
} from '@applyboard/ui-icons';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { RemoveScroll } from 'react-remove-scroll';

import { useFormattedIntakeDetails } from '~/utils/useFormattedIntakeDetails';

import {
  useApplication,
  useApplicationEslIntake,
  useApplicationEslProgram,
  useApplicationEslSchool,
  useApplicationMainIntake,
  useApplicationMainProgram,
  useApplicationMainSchool,
  useApplicationNotices,
  useApplicationStudent,
} from '../../data';
import {
  GLOBAL_TOP_BAR_HEIGHT,
  MAX_PAGE_WIDTH,
  NAV_BAR_WIDTH,
  MINI_HEADER_HEIGHT,
  GUTTER_WIDTH_TOKEN,
} from '../../consts';
import { ApplicationStepperDrawerButton } from '../ApplicationStepper';
import { SidebarDrawerButton } from '../SideBar';
import { useParams } from 'react-router-dom';
import { ApplicationNotice } from '~/types/ApplicationNotice';

export type HeaderAttributes = Array<
  'student' | 'studentType' | 'recruitmentPartner' | 'studentApplications' | 'programInformation'
>;

const HeaderContext = createContext<{
  sidebar: React.ReactNode;
  threeSixtySolutionsAd: React.ReactNode;
}>({ sidebar: null, threeSixtySolutionsAd: null });

export function HeaderProvider({
  children,
  sidebar,
  threeSixtySolutionsAd,
}: {
  children: React.ReactNode;
  sidebar: React.ReactNode;
  threeSixtySolutionsAd: React.ReactNode;
}) {
  return (
    <HeaderContext.Provider value={{ sidebar, threeSixtySolutionsAd }}>
      {children}
    </HeaderContext.Provider>
  );
}

export function Header({
  hiddenAttributes,
  manageApplicationButton,
  SelfServeStudentShareAccessButton,
}: {
  hiddenAttributes?: HeaderAttributes;
  manageApplicationButton?: React.ReactElement;
  SelfServeStudentShareAccessButton?: ComponentType<{
    studentUserId: number;
    showIcon?: boolean;
  }>;
}) {
  const { requirement_id: requirementId } = useParams<{ requirement_id?: string }>();
  const { mainProgramDetails } = useApplicationMainProgram();
  const { mainSchool } = useApplicationMainSchool();
  const { application } = useApplication();
  const { t } = useTranslation('Header');

  const { elementRef, hasScrolledOutOfView } = useHasElementScrolledOutOfView({
    topMargin: GLOBAL_TOP_BAR_HEIGHT,
  });
  const isNewLayoutEnabled = !!application.newView;
  const shouldShowMiniHeader = isNewLayoutEnabled && hasScrolledOutOfView;
  const { sidebar, threeSixtySolutionsAd } = useContext(HeaderContext);

  const notices = useHighImportanceApplicationWarnings();

  const actions = (
    <Flex gap={2}>
      {manageApplicationButton}
      {SelfServeStudentShareAccessButton && (
        <SelfServeStudentShareAccessButton studentUserId={application.student.id} showIcon={true} />
      )}
    </Flex>
  );

  return (
    <>
      <header
        css={theme => ({
          width: '100%',
          background: theme.colors.bgRegular,
          borderBottom: notices.length === 0 ? `1px solid ${theme.colors.borderRegular}` : 'none',
          borderTop: `1px solid ${theme.colors.borderRegular}`,
          position: 'relative',
        })}
        aria-label={t('headerLabel')}
      >
        <MiniHeader show={shouldShowMiniHeader} actions={actions} />
        <div
          style={
            isNewLayoutEnabled
              ? {
                  maxWidth: MAX_PAGE_WIDTH,
                  margin: 'auto',
                }
              : {}
          }
        >
          <GridAlpha
            px={6}
            py={{ xs: 6, sm: 8 }}
            columns={{ xs: '1fr auto', md: '72px 1fr auto' }}
            columnGap={6}
            rows={{
              xs: 'repeat(3, auto)',
              md: 'repeat(2, auto)',
            }}
            rowGap={4}
            areas={{
              xs: ['schoolLogo actions', 'programName programName', 'attributes attributes'],
              md: ['schoolLogo programName actions', 'schoolLogo attributes attributes'],
            }}
          >
            <GridAlpha.Item areaName="schoolLogo">
              <div
                aria-hidden={shouldShowMiniHeader}
                style={{ visibility: shouldShowMiniHeader ? 'hidden' : 'visible' }}
              >
                <SchoolLogo />
              </div>
            </GridAlpha.Item>
            <GridAlpha.Item areaName="programName">
              <div
                aria-hidden={shouldShowMiniHeader}
                style={{ visibility: shouldShowMiniHeader ? 'hidden' : 'visible' }}
              >
                <Flex direction="column" gap={2}>
                  <Flex direction="column">
                    <Text variant="labelL" contrast="mid">
                      {mainSchool.name}
                    </Text>
                    <Heading level={1} variant="headlineM">
                      {mainProgramDetails.name}
                      <div
                        ref={elementRef}
                        style={{
                          position: 'relative',
                          top: -MINI_HEADER_HEIGHT,
                        }}
                      />
                    </Heading>
                  </Flex>
                  <HeaderTags />
                </Flex>
              </div>
            </GridAlpha.Item>
            <GridAlpha.Item areaName="actions">
              <div style={{ visibility: shouldShowMiniHeader ? 'hidden' : 'visible' }}>
                {actions}
              </div>
            </GridAlpha.Item>
            <GridAlpha.Item areaName="attributes">
              <Flex gap={6} direction="column">
                <ApplicationAttributes hiddenAttributes={hiddenAttributes} />
                {isNewLayoutEnabled && (
                  <Flex gap={2} wrap>
                    {sidebar && (
                      <div
                        css={{
                          flexGrow: 0,
                          [`@media (min-width: ${requirementId ? '1280px' : '1120px'})`]: {
                            display: 'none',
                          },
                          '@media (max-width: 360px)': {
                            flexGrow: 1,
                          },
                        }}
                      >
                        <SidebarDrawerButton sidebar={sidebar} width="fill" />
                      </div>
                    )}
                    <div
                      css={{
                        '@media (max-width: 360px)': {
                          flexGrow: 1,
                        },
                        ...(!requirementId
                          ? {
                              '@media (min-width: 1508px)': {
                                display: 'none',
                              },
                            }
                          : {}),
                      }}
                    >
                      <ApplicationStepperDrawerButton
                        threeSixtySolutionsAd={threeSixtySolutionsAd}
                        width="fill"
                      />
                    </div>
                  </Flex>
                )}
              </Flex>
            </GridAlpha.Item>
          </GridAlpha>
        </div>
        {isNewLayoutEnabled &&
          notices.map(notice => <FullWidthApplicationWarning key={notice.id} notice={notice} />)}
      </header>
    </>
  );
}

function useHighImportanceApplicationWarnings() {
  const { warningNotices } = useApplicationNotices({ regarding: 'application' });
  return warningNotices.filter(
    notice => notice.importance === 'high' && notice.id !== 'duplicate-application-warning',
  );
}

function FullWidthApplicationWarning({ notice }: { notice: ApplicationNotice }) {
  const { trackEvent } = useEventTracker();
  return (
    <div
      css={{
        backgroundColor: '#fbeecc',
        width: '100%',
      }}
    >
      <div
        css={theme => ({
          maxWidth: MAX_PAGE_WIDTH,
          margin: 'auto',
          paddingLeft: theme.spacing[GUTTER_WIDTH_TOKEN],
          paddingRight: theme.spacing[GUTTER_WIDTH_TOKEN],
          paddingTop: theme.spacing[4],
          paddingBottom: theme.spacing[4],
        })}
      >
        <Flex gap={2}>
          <WarningOutlineIcon intent="warning" />
          <Markdown
            markdown={notice.message}
            headingSettings={{ maxLevel: 0, baseLevel: 1 }}
            textSettings={{
              intent: 'warning',
            }}
            linkSettings={{
              onClick: ({ text }) => {
                if (
                  notice.id === 'pending-payment-warning' &&
                  text.toLowerCase() === 'submit payment now'
                ) {
                  trackEvent({
                    event: 'Single Application V2 - Prepayment Banner - Clicked Submit Payment Now',
                  });
                }
              },
            }}
          />
        </Flex>
      </div>
    </div>
  );
}

function MiniHeader({ show, actions }: { show: boolean; actions?: React.ReactNode }) {
  const { mainProgramDetails } = useApplicationMainProgram();
  const { mainSchool } = useApplicationMainSchool();

  return (
    <div
      // See: https://github.com/radix-ui/primitives/discussions/1586#discussioncomment-3302669
      // See: https://github.com/theKashey/react-remove-scroll#positionfixed-elements
      className={RemoveScroll.classNames.zeroRight}
      css={theme => ({
        position: 'fixed',
        top: GLOBAL_TOP_BAR_HEIGHT,
        left: NAV_BAR_WIDTH,
        right: 0,
        zIndex: 2,
        height: MINI_HEADER_HEIGHT,
        backgroundColor: 'white',
        borderBottom: `1px solid ${theme.colors.borderRegular}`,
        paddingTop: theme.spacing[2],
        paddingBottom: theme.spacing[2],
        display: show ? 'block' : 'none',
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.20)',
        '@media (max-width: 768px)': {
          left: 0,
        },
      })}
    >
      <div
        css={theme => ({
          width: '100%',
          maxWidth: MAX_PAGE_WIDTH,
          margin: 'auto',
          paddingLeft: theme.spacing[GUTTER_WIDTH_TOKEN],
          paddingRight: theme.spacing[GUTTER_WIDTH_TOKEN],
          height: '100%',
        })}
      >
        <Flex height="fill" justify="between" align="center" gap={4}>
          <Flex height="fill" gap={4} align="center" minWidth={0}>
            <SchoolLogo height="100%" />
            <Text overflow="ellipsis" contrast="mid" variant="titleS">
              <Text variant="titleM" as="span">
                <h1
                  style={{
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    color: 'inherit',
                    fontFamily: 'inherit',
                    display: 'inline',
                  }}
                >
                  {mainProgramDetails.name}
                </h1>
              </Text>
              <Text variant="titleS" contrast="mid" as="span">
                {' | '}
                {mainSchool.name}
              </Text>
            </Text>
          </Flex>
          {actions}
        </Flex>
      </div>
    </div>
  );
}

function SchoolLogo({ height = 48 }: { height?: '100%' | 48 }) {
  const { mainSchool } = useApplicationMainSchool();
  const [showFallback, setShowFallback] = useState(false);
  const url = mainSchool.logo?.url;

  if (showFallback || !url) {
    if (height === '100%') {
      return null;
    }
    return (
      <div
        css={theme => ({
          background: theme.colors.bgSecondary,
          height,
          aspectRatio: '1/1',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '@media (min-width: 600px)': {
            height: 72,
            width: 72,
          },
        })}
      >
        <SchoolHifiIcon intent="secondary" aria-label="" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={`Logo of ${mainSchool.name}`}
      css={{
        width: 'auto',
        height,
        '@media (min-width: 600px)': height !== '100%' && {
          height: 'auto',
          width: 72,
        },
      }}
      onError={() => setShowFallback(true)}
    />
  );
}

function HeaderTags() {
  const { t } = useTranslation('Header');
  const { warningNotices } = useApplicationNotices();
  const { application } = useApplication();
  const { enrollmentConfirmed, instantSubmissionEnabled } = application.flags;
  const duplicateWarning = warningNotices.find(
    notice => notice.id === 'duplicate-application-warning',
  );
  if (!duplicateWarning && !enrollmentConfirmed && !instantSubmissionEnabled) {
    return null;
  }
  return (
    <Flex gap={4} wrap>
      {enrollmentConfirmed && (
        <Tag as="button" intent="positive" icon="fill" size="md">
          {t('enrollmentConfirmed')}
        </Tag>
      )}
      {instantSubmissionEnabled && (
        <Tooltip label={t('instantSubmission.message')} placement="bottom">
          <Tag as="button" intent="primary" icon="fill" size="md" trailIcon={InfoOutlineIcon}>
            {t('instantSubmission.label')}
          </Tag>
        </Tooltip>
      )}
      {duplicateWarning && (
        <Tooltip label={duplicateWarning.message}>
          <Tag
            as="button"
            intent={duplicateWarning.intent}
            icon="fill"
            size="md"
            trailIcon={InfoOutlineIcon}
          >
            {t('duplicatedApplication')}
          </Tag>
        </Tooltip>
      )}
    </Flex>
  );
}

function ApplicationAttributes({ hiddenAttributes = [] }: { hiddenAttributes?: HeaderAttributes }) {
  const { t } = useTranslation('Header');
  const overflowContainerRef = useRef<HTMLDivElement>(null);
  const { applicationAttributes } = useApplicationAttributes({ hiddenAttributes });
  const visibleAttributes = applicationAttributes.filter(attribute => !attribute.isHidden);
  const { itemsRef, hiddenItems } = useHideElementsOnOverflow({
    items: visibleAttributes,
    overflowContainer: overflowContainerRef,
  });
  const showAttributesDialog = hiddenItems.some(item => item);
  return (
    <Flex gap={6}>
      <div ref={overflowContainerRef} css={{ overflowX: 'hidden' }}>
        <DescriptionList variant="free" aria-label={t('applicationAttributes')}>
          <Flex gap={10}>
            {visibleAttributes.map((attribute, index) => (
              <div
                key={index}
                ref={element => (itemsRef.current[index] = element)}
                css={{
                  maxWidth: 280,
                  visibility: hiddenItems[index] ? 'hidden' : 'visible',
                }}
              >
                <Flex direction="column" gap={1}>
                  <DescriptionList.Term>
                    <span css={{ textWrap: 'nowrap' }}>{attribute.term}</span>
                  </DescriptionList.Term>
                  <DescriptionList.Details>
                    <Text overflow="ellipsis">{attribute.detail}</Text>
                  </DescriptionList.Details>
                </Flex>
              </div>
            ))}
          </Flex>
        </DescriptionList>
      </div>
      {showAttributesDialog && (
        <Flex.Item>
          <ApplicationAttributesDialog hiddenAttributes={hiddenAttributes} />
        </Flex.Item>
      )}
    </Flex>
  );
}

function ApplicationAttributesDialog({
  hiddenAttributes = [],
}: {
  hiddenAttributes?: HeaderAttributes;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('Header');
  const { applicationAttributes } = useApplicationAttributes({ hiddenAttributes });
  const visibleAttributes = applicationAttributes.filter(attribute => !attribute.isHidden);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger>
        <Button size="sm" intent="primary" emphasis="transparent" trailIcon={ArrowRightOutlineIcon}>
          {t('applicationAttributesDialog.button')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Heading>{t('applicationAttributesDialog.title')}</Dialog.Heading>
      <Dialog.Content>
        <DescriptionList aria-label={t('applicationAttributes')} variant="free">
          <GridAlpha columns="auto 1fr" columnGap={4} rowGap={4}>
            {visibleAttributes.map((attribute, index) => (
              <Fragment key={index}>
                <DescriptionList.Term>{attribute.term}</DescriptionList.Term>
                <DescriptionList.Details>{attribute.detail}</DescriptionList.Details>
              </Fragment>
            ))}
          </GridAlpha>
        </DescriptionList>
      </Dialog.Content>
      <Dialog.Actions>
        <Flex justify="end" grow={1}>
          <Dialog.CloseButton
            intent="primary"
            emphasis="outlined"
            width={{ xs: 'fill', sm: 'hug' }}
          >
            {t('applicationAttributesDialog.close')}
          </Dialog.CloseButton>
        </Flex>
      </Dialog.Actions>
    </Dialog>
  );
}

function useApplicationAttributes({ hiddenAttributes }: { hiddenAttributes: HeaderAttributes }) {
  const { t } = useTranslation('Header');
  const { application } = useApplication();
  const { applicationMainProgram } = useApplicationMainProgram();
  const { mainIntake } = useApplicationMainIntake();
  const { mainSchool } = useApplicationMainSchool();
  const { eslIntake } = useApplicationEslIntake();
  const { eslProgramDetails, applicationEslProgram } = useApplicationEslProgram();
  const { eslSchool } = useApplicationEslSchool();
  const { student } = useApplicationStudent();

  const mainIntakeDetails = useFormattedIntakeDetails({
    startDate: applicationMainProgram.startDate,
    intake: mainIntake,
  });

  const eslIntakeDetails = useFormattedIntakeDetails({
    startDate: applicationEslProgram?.startDate,
    intake: eslIntake,
  });

  const applicationAttributes = [
    {
      term: t('applicationNumber'),
      detail: application.legacyId,
    },
    {
      term: t('academicIntake'),
      detail: (
        <Flex gap={2}>
          <Text overflow="ellipsis">
            {mainIntakeDetails.formattedDate}
            {mainIntakeDetails.status ? ', ' : ' '}
            {mainIntakeDetails.status && (
              <Text
                as="span"
                contrast={mainIntakeDetails.status.contrast}
                intent={mainIntakeDetails.status.intent}
              >{`${mainIntakeDetails.status.label}`}</Text>
            )}
          </Text>
          {mainIntakeDetails && mainIntakeDetails.status?.infoText && (
            <Tooltip label={mainIntakeDetails.status.infoText}>
              <Tooltip.Icon intent="secondary" />
            </Tooltip>
          )}
        </Flex>
      ),
      isHidden: !applicationMainProgram.startDate,
    },
    {
      term: t('eslIntake'),
      detail: (
        <Flex gap={2}>
          <Text overflow="ellipsis">
            {eslIntakeDetails.formattedDate}
            {eslIntakeDetails.status ? ', ' : ' '}
            {eslIntakeDetails.status && (
              <Text
                as="span"
                contrast={eslIntakeDetails.status.contrast}
                intent={eslIntakeDetails.status.intent}
              >{`${eslIntakeDetails.status.label}`}</Text>
            )}
          </Text>
          {eslIntakeDetails && eslIntakeDetails.status?.infoText && (
            <Tooltip label={eslIntakeDetails.status.infoText}>
              <Tooltip.Icon intent="secondary" />
            </Tooltip>
          )}
        </Flex>
      ),
      isHidden: !applicationEslProgram?.startDate,
    },
    {
      term: t('student'),
      detail: (
        <Flex gap={4}>
          <Text overflow="ellipsis">
            <Link href={`/students/${application.student.id}`}>{application.student.fullName}</Link>
          </Text>
          {mainSchool.countryCode === student.profile.residentialAddress.address.country && (
            <Tag intent="secondary">{t('onshore')}</Tag>
          )}
        </Flex>
      ),
      isHidden: hiddenAttributes.includes('student'),
    },
    {
      term: t('studentType'),
      detail: (
        <Tag
          intent={application.legacyRpUserId ? 'secondary' : 'positive'}
          icon={application.legacyRpUserId ? PersonTieRelationshipOutlineIcon : PersonOutlineIcon}
        >
          {t(application.legacyRpUserId ? 'indirect' : 'direct')}
        </Tag>
      ),
      isHidden: hiddenAttributes.includes('studentType'),
    },
    {
      term: t('studentApplications'),
      detail: (
        <Text overflow="ellipsis">
          <Link href={`/students/${application.student.id}/applications`}>
            {t('applicationCount', { count: student.totalApplications })}
          </Link>
        </Text>
      ),
      isHidden: hiddenAttributes.includes('studentApplications'),
    },
    {
      term: t('recruitmentPartner'),
      detail: (
        <Text overflow="ellipsis">
          <Link href={`/agents/${application.legacyRpUserId}`}>{application.rpFullName}</Link>
        </Text>
      ),
      isHidden:
        hiddenAttributes.includes('recruitmentPartner') ||
        !application.legacyRpUserId ||
        !application.rpFullName,
    },

    {
      term: t('academicSchool'),
      detail: (
        <Text overflow="ellipsis">
          <Link href={`/schools/${mainSchool.slug}`}>{mainSchool.name}</Link>
        </Text>
      ),
      isHidden: hiddenAttributes.includes('programInformation'),
    },
    {
      term: t('country'),
      detail: <Text overflow="ellipsis">{mainSchool.country}</Text>,
      isHidden: hiddenAttributes.includes('programInformation'),
    },

    {
      term: t('eslProgramId'),
      detail: <Text overflow="ellipsis">{eslProgramDetails?.id}</Text>,
      isHidden: hiddenAttributes.includes('programInformation') || !eslProgramDetails,
    },
    {
      term: t('eslProgram'),
      detail: (
        <Text overflow="ellipsis">
          {eslSchool?.slug && eslProgramDetails?.slug ? (
            <Link href={`/schools/${eslSchool.slug}/programs/${eslProgramDetails?.slug}`}>
              {eslProgramDetails?.name}
            </Link>
          ) : (
            eslProgramDetails?.name
          )}
        </Text>
      ),
      isHidden: hiddenAttributes.includes('programInformation') || !eslProgramDetails,
    },
    {
      term: t('eslProgramSchool'),
      detail: (
        <Text overflow="ellipsis">
          <Link href={`/schools/${eslSchool?.slug}`}>{eslSchool?.name}</Link>
        </Text>
      ),
      isHidden: hiddenAttributes.includes('programInformation') || !eslSchool,
    },
  ];
  return {
    applicationAttributes,
  };
}

/**
 * Set items in an array of elements to hidden if they overflow the container
 */
function useHideElementsOnOverflow({
  items,
  overflowContainer,
}: {
  items: ReturnType<typeof useApplicationAttributes>['applicationAttributes'];
  overflowContainer: RefObject<HTMLElement>;
}) {
  const itemsRef = useRef<Array<HTMLDivElement | null>>([]);
  const [hiddenItems, setHiddenItems] = useState<boolean[]>([]);

  // Need useEffect to handle the intersection observer
  // eslint-disable-next-line
  useEffect(() => {
    if (itemsRef.current && overflowContainer.current) {
      itemsRef.current = itemsRef.current.slice(0, items.length);

      const observer = new IntersectionObserver(
        entries => {
          setHiddenItems(entries.map(entry => !entry.isIntersecting));
        },
        { root: overflowContainer.current, threshold: 1 },
      );

      itemsRef.current.forEach(item => {
        if (item) {
          observer.observe(item);
        }
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [items, overflowContainer]);

  return {
    hiddenItems,
    itemsRef,
  };
}
